import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Grid,
  Paper
} from '@mui/material';
import './invoice-print.css';

const InvoicePrint = ({ invoice, client, tenant, isPrintMode = false }) => {
  // Standalone currency formatter (no context dependency)
  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: '#757575',
      sent: '#1976d2',
      paid: '#2e7d32',
      overdue: '#d32f2f',
      cancelled: '#d32f2f'
    };
    return colors[status] || '#757575';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  // Calculate totals
  const subtotal = invoice.items?.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) || 0;
  const gstAmount = invoice.gst_amount || 0;
  const discountAmount = invoice.discount_amount || 0;
  const membershipDiscountAmount = invoice.membership_discount_amount || 0;
  const totalAmount = invoice.total_amount || 0;
  const paidAmount = invoice.total_paid || 0;
  const balanceAmount = totalAmount - paidAmount;

  return (
    <Box
      className="invoice-print-container"
      sx={{
        p: isPrintMode ? 0 : 3,
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
          {tenant.company_name != '' ? tenant.company_name : tenant.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tenant?.invoice_phone && (
            <>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Phone:</Box> {tenant.invoice_phone}
            </>
          )}
          {tenant?.invoice_mobile && (
            <>
              <Box component="span" sx={{ fontWeight: 'bold' }}> Mobile:</Box> {tenant.invoice_mobile}
            </>
          )}
          {tenant?.invoice_email && (
            <>
              <Box component="span" sx={{ fontWeight: 'bold' }}> Email:</Box> {tenant.invoice_email}
            </>
          )}
          {!tenant?.invoice_phone && !tenant?.invoice_email && (
            <>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Contact:</Box> +91 98765 43210 | <Box component="span" sx={{ fontWeight: 'bold' }}>Email:</Box> info@salon.com
            </>
          )}
        </Typography>
        <>

          <Typography variant="body2" color="text.secondary">
            {tenant?.invoice_address_line1 && (
              tenant.invoice_address_line1 + ' ' + tenant.invoice_address_line2 + ' ' + tenant.invoice_city + ' ' + tenant.invoice_state + ' ' + tenant.invoice_zip_code + ' ' + tenant.invoice_country
            )}
          </Typography>


        </>
        {tenant?.website && (
          <Typography variant="body2" color="text.secondary">
            Website: {tenant.website}
          </Typography>
        )}
        {tenant?.gst_number && (
          <Typography variant="body2" color="text.secondary">
            GST: {tenant.gst_number}
          </Typography>
        )}
      </Box>

      {/* Invoice Details */}
      <Grid spacing={3} sx={{ mb: 4 }} style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Grid item xs={6} style={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            INVOICE
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoice #: {invoice.invoice_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: {formatDate(invoice.invoice_date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status:
            <span style={{
              color: getStatusColor(invoice.status),
              fontWeight: 'bold',
              marginLeft: '8px'
            }}>
              {getStatusLabel(invoice.status)}
            </span>
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            BILL TO
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {client?.first_name} {client?.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mobile: {client?.mobile}
          </Typography>
          {client?.email && (
            <Typography variant="body2" color="text.secondary">
              Email: {client.email}
            </Typography>
          )}
          {client?.address && (
            <Typography variant="body2" color="text.secondary">
              Address: {client.address}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Items Table */}
      <Table sx={{ mb: 4 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Price</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">GST %</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.items?.map((item, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {item.item_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {item.item_type}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {item.quantity}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {formatPrice(item.unit_price)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                  {item.gst_percentage ? `${item.gst_percentage}%` : '-'}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ textAlign: 'right' }}>
                <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'right' }}>
                  {formatPrice(item.total_price)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ minWidth: '300px' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 'none', py: 0.5 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                </TableCell>
                <TableCell align="right" sx={{ border: 'none', py: 0.5, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatPrice(subtotal)}</Typography>
                </TableCell>
              </TableRow>
              {discountAmount > 0 && (
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.5 }}>
                    <Typography variant="body2" color="error">
                      Discount ({invoice.discount_type === 'percentage' ? `${invoice.discount_value}%` : 'Amount'}):
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none', py: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2" color="error" sx={{ textAlign: 'right' }}>
                      -{formatPrice(discountAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {membershipDiscountAmount > 0 && (
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.5 }}>
                    <Typography variant="body2" color="success.main">
                      Membership Discount:
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none', py: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2" color="success.main" sx={{ textAlign: 'right' }}>
                      -{formatPrice(membershipDiscountAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {gstAmount > 0 && (
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.5 }}>
                    <Typography variant="body2">GST:</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none', py: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatPrice(gstAmount)}</Typography>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ border: 'none', py: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total Amount:
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ border: 'none', py: 1, textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ textAlign: 'right' }}>
                    {formatPrice(totalAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
              {paidAmount > 0 && (
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.5 }}>
                    <Typography variant="body2" color="success.main">
                      Amount Paid:
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none', py: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2" color="success.main" sx={{ textAlign: 'right' }}>
                      {formatPrice(paidAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {balanceAmount > 0 && (
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      Balance:
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none', py: 1, textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight="bold" color="error.main" sx={{ textAlign: 'right' }}>
                      {formatPrice(balanceAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Payment History
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mode</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.payments.map((payment, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(payment.payment_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.payment_mode}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(payment.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {payment.reference_number || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {/* Notes */}
      {invoice.notes && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoice.notes}
          </Typography>
        </>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Thank you for your business!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          For any queries, please contact us at {tenant?.invoice_mobile} or {tenant?.invoice_email}
        </Typography>
      </Box>
    </Box>
  );
};

export default InvoicePrint; 